import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Mail, Users, IdCard } from "lucide-react";
import { 
  PasswordInput,
  ForgotPasswordFlow,
  OTPVerification,
  SetPasswordForm,
  RegistrationSuccess,
  RegistrationStep1,
  RegistrationStep2,
  RegistrationStep3
} from "./forms";

export function ParentLoginForm({ onBack, startInRegisterMode = false, selectedPlan = null }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(startInRegisterMode ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: login, 2: otp, 3: set-password
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetMethod, setResetMethod] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetStep, setResetStep] = useState(1);
  const [needsPassword, setNeedsPassword] = useState(false);
  
  // Registration state
  const [regStep, setRegStep] = useState(1);
  const [parentName, setParentName] = useState("");
  const [regOtp, setRegOtp] = useState("");
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState("");
  const [childGrade, setChildGrade] = useState("");
  const [board, setBoard] = useState("cbse");
  const [schoolName, setSchoolName] = useState("");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [parentalConsent, setParentalConsent] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(null);
  const [plan] = useState("free_trial");

  const toggleSubject = (subjectId) => {
    setSelectedSubjects(prev => 
      prev.includes(subjectId) 
        ? prev.filter(s => s !== subjectId)
        : [...prev, subjectId]
    );
  };

  const checkAccount = async () => {
    try {
      const res = await fetch(`${API}/parent/auth/check-account?email=${encodeURIComponent(email)}`);
      const data = await res.json();
      return data;
    } catch {
      return null;
    }
  };

  // Registration handlers
  const handleRegisterStart = async () => {
    if (!parentName.trim() || !email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/register/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_name: parentName, parent_email: email.trim().toLowerCase() })
      });
      const data = await res.json();
      if (data.success) {
        // Check if OTP is auto-verified (2FA disabled)
        if (data.otp_verified) {
          toast.success("Email verified. Continue with registration.");
          setRegStep(3); // Skip OTP step, go directly to child details
        } else {
          toast.success("Verification code sent to your email");
          setRegStep(2);
        }
      } else {
        toast.error(data.detail || "Failed to start registration");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRegOtp = async () => {
    if (!regOtp.trim() || regOtp.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/register/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp_code: regOtp.trim() })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Email verified! Now add your child's details");
        setRegStep(3);
      } else {
        toast.error(data.detail || "Verification failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = async (newEmail) => {
    if (!newEmail.trim() || !newEmail.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    try {
      const res = await fetch(`${API}/parent/auth/register/change-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          old_email: email.trim().toLowerCase(), 
          new_email: newEmail.trim().toLowerCase(),
          parent_name: parentName 
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Email updated! Check your new email for the verification code");
        setEmail(newEmail.trim().toLowerCase());
        setRegOtp(""); // Clear old OTP
      } else {
        toast.error(data.detail || "Failed to change email");
        throw new Error(data.detail || "Failed to change email");
      }
    } catch (error) {
      if (!error.message.includes("Failed to change")) {
        toast.error("Network error. Please try again.");
      }
      throw error;
    }
  };

  const handleCompleteRegistration = async () => {
    if (!childName.trim() || !childGrade || !board || !password || selectedSubjects.length === 0) {
      toast.error("Please fill all required fields");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!acceptTerms) {
      toast.error("Please accept the terms and conditions");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/register/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          otp_code: "000000",  // Placeholder since 2FA is disabled
          password,
          child_name: childName,
          child_age: parseInt(childAge) || 10,
          child_grade: parseInt(childGrade),
          board: board,
          school_name: schoolName || "Not Specified",
          subjects: selectedSubjects,
          accept_terms: acceptTerms,
          subscription_plan: plan
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Registration successful!");
        setRegistrationSuccess({
          student_id: data.student_id,
          child_name: childName
        });
        setRegStep(1);
        setRegOtp("");
        setChildName("");
        setChildAge("");
        setChildGrade("");
        setSchoolName("");
        setSelectedSubjects([]);
        setAcceptTerms(false);
        setPassword("");
      } else {
        toast.error(data.detail || "Registration failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Please enter both your Parent ID and password");
      return;
    }
    setLoading(true);
    try {
      const account = await checkAccount();
      if (account && account.exists && !account.has_password) {
        setNeedsPassword(true);
        setStep(3);
        setLoading(false);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      let res, data = {};
      try {
        res = await fetch(`${API}/parent/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ identifier: email.trim().toUpperCase(), password }),
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        data = await res.json();
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (res) {
          if (res.status === 401) {
            toast.error("Incorrect password. Please try again.");
            return;
          } else if (res.status === 404) {
            toast.error("No account found with this Parent ID.");
            return;
          } else if (res.status === 423) {
            toast.error("Account locked due to too many failed attempts. Please reset your password.");
            setShowForgotPassword(true);
            return;
          } else if (!res.ok) {
            toast.error("Login failed. Please try again.");
            return;
          }
        }
        console.error("Login fetch error:", fetchError);
        toast.error("Network error. Please check your connection.");
        return;
      }
      
      if (res.ok && data.success && data.requires_otp) {
        toast.success("Verification code sent to your email");
        setStep(2);
      } else if (res.ok && data.success && !data.requires_otp) {
        // 2FA is disabled - direct login successful
        toast.success("Login successful!");
        setTimeout(() => {
          navigate("/parent/dashboard");
        }, 100);
      } else if (res.status === 401) {
        toast.error(data.detail || "Incorrect password. Please try again.");
      } else if (res.status === 404) {
        toast.error(data.detail || "No account found with this Parent ID.");
      } else if (res.status === 423) {
        toast.error(data.detail || "Account locked. Please reset your password.");
        setShowForgotPassword(true);
      } else if (!res.ok) {
        toast.error(data.detail || "Login failed. Please try again.");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim() || otp.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/verify-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp_code: otp.trim() })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Login successful!");
        navigate("/parent/dashboard");
      } else {
        toast.error(data.detail || "Verification failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Password set! Please login now.");
        setNeedsPassword(false);
        setStep(1);
        setPassword("");
      } else {
        toast.error(data.detail || "Failed to set password");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      toast.error("Please enter your Parent ID first");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), method: resetMethod })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Reset code sent via ${resetMethod}`);
        setResetStep(2);
      } else {
        toast.error(data.detail || "Failed to send reset code");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode.trim() || !newPassword.trim()) {
      toast.error("Please fill all fields");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email: email.trim().toLowerCase(), 
          otp_code: resetCode.trim(),
          new_password: newPassword 
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Password reset successfully!");
        setShowForgotPassword(false);
        setResetStep(1);
        setResetCode("");
        setNewPassword("");
      } else {
        toast.error(data.detail || "Reset failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  // ===== FORGOT PASSWORD =====
  if (showForgotPassword) {
    return (
      <ForgotPasswordFlow
        email={email}
        setEmail={setEmail}
        resetMethod={resetMethod}
        setResetMethod={setResetMethod}
        resetCode={resetCode}
        setResetCode={setResetCode}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        resetStep={resetStep}
        loading={loading}
        onBack={() => { setShowForgotPassword(false); setResetStep(1); }}
        onSendCode={handleForgotPassword}
        onResetPassword={handleResetPassword}
      />
    );
  }

  // ===== SET PASSWORD (First-time users) =====
  if (step === 3) {
    return (
      <SetPasswordForm
        email={email}
        password={password}
        setPassword={setPassword}
        loading={loading}
        onBack={() => { setStep(1); setNeedsPassword(false); }}
        onSubmit={handleSetPassword}
      />
    );
  }

  // ===== OTP VERIFICATION =====
  if (step === 2) {
    return (
      <OTPVerification
        email={email}
        otp={otp}
        setOtp={setOtp}
        loading={loading}
        onBack={() => setStep(1)}
        onVerify={handleVerifyOTP}
        onResend={() => handleLogin({ preventDefault: () => {} })}
      />
    );
  }

  // ===== REGISTRATION MODE =====
  if (mode === "register") {
    // Registration Success
    if (registrationSuccess) {
      return (
        <RegistrationSuccess 
          data={registrationSuccess}
          onLogin={() => { setMode("login"); setRegistrationSuccess(null); setRegStep(1); }}
        />
      );
    }

    // Step 1: Parent Details
    if (regStep === 1) {
      return (
        <RegistrationStep1
          parentName={parentName}
          setParentName={setParentName}
          email={email}
          setEmail={setEmail}
          loading={loading}
          onContinue={handleRegisterStart}
        />
      );
    }

    // Step 2: Verify OTP
    if (regStep === 2) {
      return (
        <RegistrationStep2
          email={email}
          regOtp={regOtp}
          setRegOtp={setRegOtp}
          loading={loading}
          onBack={() => setRegStep(1)}
          onVerify={handleVerifyRegOtp}
          onChangeEmail={handleChangeEmail}
        />
      );
    }

    // Step 3: Child Details + Password
    if (regStep === 3) {
      return (
        <RegistrationStep3
          childName={childName}
          setChildName={setChildName}
          childAge={childAge}
          setChildAge={setChildAge}
          childGrade={childGrade}
          setChildGrade={setChildGrade}
          board={board}
          setBoard={setBoard}
          schoolName={schoolName}
          setSchoolName={setSchoolName}
          selectedSubjects={selectedSubjects}
          toggleSubject={toggleSubject}
          password={password}
          setPassword={setPassword}
          acceptTerms={acceptTerms}
          setAcceptTerms={setAcceptTerms}
          parentalConsent={parentalConsent}
          setParentalConsent={setParentalConsent}
          loading={loading}
          onBack={() => setRegStep(2)}
          onComplete={handleCompleteRegistration}
        />
      );
    }
  }

  // ===== LOGIN MODE (Default) =====
  return (
    <form onSubmit={handleLogin} className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Users className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Parent Login</h2>
        <p className="text-gray-600 mt-2">Monitor your child's learning progress</p>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value.toUpperCase())}
            placeholder="Parent ID (e.g., P_123456)"
            className="pl-10"
            data-testid="parent-email-input"
          />
        </div>
        <p className="text-xs text-gray-500 -mt-2">
          Use your <span className="font-medium">Parent ID</span> (format P_XXXXXX), not your email.
        </p>
        
        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          data-testid="parent-password-input"
        />
      </div>

      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700"
        data-testid="parent-login-btn"
      >
        {loading ? "Verifying..." : "Continue"}
      </Button>

      <button
        type="button"
        onClick={() => setShowForgotPassword(true)}
        className="w-full text-sm text-blue-600 hover:text-blue-700"
      >
        Forgot Password?
      </button>

      <div className="text-center">
        <p className="text-sm text-gray-500">Don't have an account?</p>
        <button
          type="button"
          onClick={() => setMode("register")}
          className="text-sm font-medium text-blue-600 hover:text-blue-700"
          data-register-trigger="true"
        >
          Register Now
        </button>
      </div>
    </form>
  );
}
